import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import uuid from "react-uuid";
import ApiService from "../../Services/apiService";
import AlertService from "../../Services/alertService";
import TranslationService from "../../Services/translationService";
import MainService from "../../Services/mainService";
import ReactSelectOption from "../../Components/SelectOption/reactSelectOption";
import {
	addButtonSpinner,
	removeButtonSpinner,
	addPartialViewSpinner,
	removePartialViewSpinner,
} from "../../Store/Actions/spinner";
import { ERROR_KEY, SUCCESS_KEY, VALID_FILE_TYPES_KEY, VALID_IMAGE_TYPES_KEY } from "../../Constants/mainKeys";
import TemplateDocumentForm from "./components/TemplateDocumentForm";
import ArrowBackSvg from './../../Components/Svg/arrowBackSvg';
import ActionButton from "../../Components/ActionButton/actionButton";
import UploadButton from "../../Components/UploadButton/uploadButton";
import CloseSvg from './../../Components/Svg/closeSvg';
import RichTextEditor from "../../Pages/Questions/Components/RichTextEditor";
import "../../"

function DocumentForm(props) {
	const dispatch = useDispatch();
	const { language } = useSelector(state => state.language);
	const translations = useSelector(state => state.translation.translations);
	const [translationService, setTranslationService] = useState(null);
	const { documentId } = props.match.params || null;

	const isEdit = documentId ? true : false;

	const mainService = useMemo(() => new MainService(), []);
	const submitSpinnerId = useMemo(() => uuid(), []);

	const [categories, setCategories] = useState([]);
	const [templates, setTemplates] = useState([]);

	const [selectedCategory, setSelectedCategory] = useState(null);
	const [selectedTemplate, setSelectedTemplate] = useState(null);

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [dataText, setDataText] = useState("");

	const [uploadedFile, setUploadedFile] = useState(null);
	const [uploadedPreview, setUploadedPreview] = useState(null);
	const [existingFilePath, setExistingFilePath] = useState(null);

	const [documentBodyData, setDocumentBodyData] = useState(null);
	const [templatePayload, setTemplatePayload] = useState(null);
	const [isInvalidSubmit, setIsInvalidSubmit] = useState(false);
	const templateFormRef = useRef(null);

	useEffect(() => {
		setTranslationService(new TranslationService(translations));
	}, [translations]);

	useEffect(() => {
		getCategories();
	}, []);

	useEffect(() => {
		if (documentId) getDocument(documentId);
	}, [documentId]);

	const getCategories = () => {
		const spinnerId = uuid();
		dispatch(addPartialViewSpinner(spinnerId));
		ApiService.getDocumentCategory()
			.then(res => {
				if (res && res.data) setCategories(res.data);
			})
			.catch(err => {
				AlertService.alert(
					AlertService.checkMessageType(err?.respcode) || ERROR_KEY,
					err,
				);
			})
			.finally(() => dispatch(removePartialViewSpinner(spinnerId)));
	};

	const getTemplatesByCategoryId = categoryId => {
		if (!categoryId) return;
		ApiService.getDocumentTemplatesByCategoryId(categoryId)
			.then(res => {
				if (res && res.data) setTemplates(res.data);
			})
			.catch(err => {
				AlertService.alert(
					AlertService.checkMessageType(err?.respcode) || ERROR_KEY,
					err,
				);
			});
	};

	const getDocument = id => {
		const spinnerId = uuid();
		dispatch(addPartialViewSpinner(spinnerId));
		ApiService.getDocumentById(id)
			.then(res => {
				if (!res?.data) return;
				const d = res.data;
				setName(d.name || "");
				setDescription(d.description || "");
				setDataText(d.data || "");
				setDocumentBodyData(d.data || null);
				if (!d.data && d.filePath) {
					setExistingFilePath(d.filePath || null);
				}

				if (d.documentCategoryId) {
					setSelectedCategory({ id: d.documentCategoryId, name: "" });
					getTemplatesByCategoryId(d.documentCategoryId);
				}
				if (d.documentTemplateId) {
					setSelectedTemplate({ id: d.documentTemplateId, name: "" });
				}
			})
			.catch(err => {
				AlertService.alert(
					AlertService.checkMessageType(err?.respcode) || ERROR_KEY,
					err,
				);
			})
			.finally(() => {
				setTimeout(() => {
					dispatch(removePartialViewSpinner(spinnerId))
				}, 1000);
			});
	};

	const onPickFile = e => {
		const f = e.target.files?.[0];
		if (!f) return;

		const valid = (VALID_FILE_TYPES_KEY || []).map(x => String(x).toLowerCase());
		mainService
			.readFile(f, valid.length ? valid : ["png", "jpg", "jpeg", "pdf"])
			.then(url => {
				setUploadedFile(f);
				setUploadedPreview(url);
				setSelectedTemplate(null);
				setTemplatePayload(null);
				setDataText("");
			})
			.catch(() => {
				AlertService.alert(
					ERROR_KEY,
					translationService?.translate("TR_INVALID_FILE_FORMAT_EXC") ||
					"Invalid file format",
				);
			});
	};

	const uploadFile = (event) => {
		const file = event.target.files[0];
		if (file) {
			const fileName = file.name;
			mainService
				.readFile(file, VALID_IMAGE_TYPES_KEY)
				.then(uploadedFile => {
					const currentFile = {
						file,
						fileName,
						uploadedFile,
					};
					setUploadedFile(currentFile);
				})
				.catch(error => {
					AlertService.alert(
						ERROR_KEY,
						translationService.translate("TR_VALID_IMAGE_ERROR_MSG"),
					);
				});
		}
	};

	const removeFile = () => {
		setUploadedFile(null);
		setUploadedPreview(null);
		setExistingFilePath(null);
	};

	const onSubmit = () => {
		setIsInvalidSubmit(false);
		if ((!selectedCategory?.id && !(uploadedFile || existingFilePath)) || !name.trim()) {
			setIsInvalidSubmit(true);
			MainService.scrollToInvalidFieldPosition();
			return;
		}

		const formData = new FormData();
		formData.append("name", name.trim());
		if (documentId) formData.append("id", String(documentId));
		if (selectedCategory) formData.append("documentCategoryId", String(selectedCategory.id));
		if (description.trim()) formData.append("description", description.trim());
		if (uploadedFile) {
			formData.append("documentFile", uploadedFile.file);
		} else if (selectedTemplate?.id) {
			formData.append("documentTemplateId", String(selectedTemplate.id));
			const payload = templateFormRef.current?.getPayload?.() || templatePayload;
			if (payload) {
				formData.append("data", JSON.stringify(payload));
			} else if (documentBodyData) {
				formData.append("data", documentBodyData);
			}
		} else if (dataText?.trim()) {
			formData.append("data", dataText);
		}
		dispatch(addButtonSpinner(submitSpinnerId));
		(isEdit ? ApiService.updateDocument(formData) : ApiService.createDocument(formData))
			.then(() => {
				AlertService.alert(
					SUCCESS_KEY,
					translationService?.translate(
						isEdit ? "TR_DOCUMENT_SUCCESSFULLY_UPDATED" : "TR_DOCUMENT_SUCCESSFULLY_CREATED",
					) || "Saved",
				);
				props.history.push(`/${language}/document`);
			})
			.catch(err => {
				AlertService.alert(
					AlertService.checkMessageType(err?.respcode) || ERROR_KEY,
					err,
				);
			})
			.finally(() => dispatch(removeButtonSpinner(submitSpinnerId)));
	};

	if (!translationService) return null;

	return (
		<div className="container mt-4">
			<div className="row">
				<div className="col-12">
					<h2 className="content-title p-0">
						<Link
							to={`/${language}/document`}
							title={translationService.translate("TR_BACK")}
							onClick={() => window.history.back()}>
							<ArrowBackSvg />
						</Link>

						{isEdit
							? translationService.translate("TR_UPDATE_DOCUMENT") || "Update document"
							: translationService.translate("TR_CREATE_DOCUMENT") || "Create document"}
					</h2>
				</div>
			</div>
			<hr />
			{
				<div className="row">
					<div className="col-12 col-md-6">
						<div className="form-group">
							<label>
								{translationService.translate("TR_DOCUMENT_TYPE") || "Type"} *
							</label>
							<ReactSelectOption
								isInvalidSubmit={isInvalidSubmit && !selectedCategory?.id}
								placeholder={translationService.translate("TR_CHOOSE") || "Choose"}
								isDisabled={documentId}
								selectedValue={(() => {
									const found = categories.find(c => c.id === selectedCategory?.id);
									return found ? { ...found, value: found.id, label: found.name } : null;
								})()}
								items={categories
									.filter(c => c.id !== selectedCategory?.id)
									.map(c => ({ ...c, value: c.id, label: c.name }))}
								onChange={item => {
									setSelectedCategory(item);
									setSelectedTemplate(null);
									setTemplatePayload(null);
									getTemplatesByCategoryId(item?.id);
								}}
							/>
						</div>
					</div>
					{
						!uploadedFile && !existingFilePath ?
							<div className="col-12 col-md-6">
								<div className="form-group">
									<label>{translationService.translate("TR_TEMPLATEE") || "Template"}</label>
									<ReactSelectOption
										placeholder={translationService.translate("TR_CHOOSE") || "Choose"}
										isDisabled={documentId}
										selectedValue={(() => {
											const found = templates.find(t => t.id === selectedTemplate?.id);
											return found ? { ...found, value: found.id, label: found.name } : null;
										})()}
										items={templates
											.filter(t => t.id !== selectedTemplate?.id)
											.map(t => ({ ...t, value: t.id, label: t.name }))}
										onChange={item => {
											setSelectedTemplate(item);
											setTemplatePayload(null);
											setUploadedFile(null);
											setUploadedPreview(null);
										}}
									/>
								</div>
							</div>
							: null
					}
				</div>
			}

			<div className="row">
				<div className="col-12 col-md-6">
					<div className="form-group">
						{uploadedFile || existingFilePath ? (
							<div className="upload-file-content">
								{
									isEdit ? null : <div onClick={removeFile}>
										<CloseSvg />
									</div>
								}
								<div
									style={{
										backgroundImage: `url(${uploadedFile ? uploadedFile.uploadedFile : existingFilePath})`,
										height: "300px",
										backgroundSize: "contain",
										backgroundPosition: "center",
										backgroundRepeat: "no-repeat",
										border: "1px solid #cccccc",
										borderRadius: "3px",
										overflow: "hidden",
										backgroundRepeat: "no-repeat",
									}}
									alt="course-image"></div>
							</div>
						) : !isEdit ? <UploadButton
							textInputClasses="pl-100"
							labelValue=""
							text={`${translationService.translate("TR_UPLOAD_FILE")}`}
							clicked={event => uploadFile(event)}
						/> : null}
					</div>
				</div>
			</div>

			<div className="row">
				<div className="col-12 col-md-6">
					<div className="form-group">
						<label>{translationService.translate("TR_NAME") || "Name"} *</label>
						<input
							className={`form-control ${isInvalidSubmit && !name.trim() ? "is-invalid" : ""}`}
							value={name}
							onChange={e => setName(e.target.value)}
						/>
					</div>
				</div>
				<div className="col-12 col-md-6">
					<div className="form-group">
						<label>
							{translationService.translate("TR_DESCRIPTION_OR_NUMBER") ||
								translationService.translate("TR_DESCRIPTION") ||
								"Description"}
						</label>
						<input
							className="form-control"
							value={description}
							onChange={e => setDescription(e.target.value)}
						/>
					</div>
				</div>
			</div>

			{
				!uploadedFile && !existingFilePath && selectedTemplate?.id ?
					<div className="row">
						<div className="col-12">
							<TemplateDocumentForm
								ref={templateFormRef}
								templateId={selectedTemplate?.id}
								documentBodyData={documentBodyData}
								onSubmitTemplate={payload => setTemplatePayload(payload)}
							/>
						</div>
					</div>
					: null
			}

			{!uploadedFile && !existingFilePath && !selectedTemplate?.id ? (
				<div className="row">
					<div className="col-12">
						<div className="form-group">
							{/* <label>{translationService.translate("TR_DATA") || "Data"}</label>
							<textarea
								className="form-control"
								rows={8}
								value={dataText}
								onChange={e => setDataText(e.target.value)}
							/> */}
							<label>{translationService.translate("TR_DATA") || "Data"}</label>
							<RichTextEditor
								value={dataText}
								onEditorChange={e => { setDataText(e.target.value) }}
							/>
						</div>
					</div>
				</div>
			) : null}

			<div className="row mt-3">
				<div className="col-12 d-flex justify-content-end">
					{/* <button className="mindalay--btn-dark" onClick={onSubmit}>
						{isEdit
							? translationService.translate("TR_UPDATE") || "Update"
							: translationService.translate("TR_CREATE") || "Create"}
					</button> */}

					<ActionButton
						type="button"
						spinnerId={submitSpinnerId}
						className="mindalay--btn-default position-relative"
						name={isEdit
							? translationService.translate("TR_UPDATE") || "Update"
							: translationService.translate("TR_CREATE") || "Create"}
						clicked={onSubmit}
					/>
				</div>
			</div>
		</div>
	);
}

export default withRouter(DocumentForm);

